/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";
import { useApiClient } from "common/ApiClientProvider";

function Basic({ noGutter }) {
  const { t } = useTranslation("pages");
  const [controller] = useMaterialUIController();
  const [isEdit, setIsEdit] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [telephone, setTelephone] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const { darkMode } = controller;
  const { getUser } = useKindeAuth();
  const apiClient = useApiClient();
  const user = getUser();

  async function getBasicInformation() {
    const basicInformation = await apiClient.get(
      `basic_informations/${encodeURIComponent(user.id)}/`
    );
    setCompanyName(basicInformation.company_name ?? "");
    setAddress(basicInformation.address ?? "");
    setTelephone(basicInformation.telephone ?? "");
    setPostalCode(basicInformation.postal_code ?? "");
  }

  async function saveBasicInformation() {
    await apiClient.put(`basic_informations/${encodeURIComponent(user.id)}/`, {
      body: {
        company_name: companyName,
        address,
        telephone,
        postal_code: postalCode,
      },
    });
    setIsEdit(false);
  }

  useEffect(() => {
    getBasicInformation();
  }, []);

  return (
    <MDBox
      component="li"
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      bgColor={darkMode ? "transparent" : "grey-100"}
      borderRadius="lg"
      p={3}
      mb={noGutter ? 0 : 1}
      mt={2}
    >
      <MDBox width="100%" display="flex" flexDirection="column">
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          mb={2}
        >
          <MDTypography variant="caption" color="text">
            {t("accountUi.companyNameLabel")}&nbsp;
            {isEdit ? (
              <MDInput
                type="text"
                label={t("accountUi.companyName")}
                defaultValue={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            ) : (
              <MDTypography variant="button" fontWeight="medium" textTransform="capitalize">
                {companyName}
              </MDTypography>
            )}
          </MDTypography>
          <MDBox display="flex" alignItems="center" mt={{ xs: 2, sm: 0 }} ml={{ xs: -1.5, sm: 0 }}>
            {isEdit ? (
              <MDButton variant="text" color="error" onClick={saveBasicInformation}>
                <Icon>done</Icon>&nbsp;{t("accountUi.save")}
              </MDButton>
            ) : (
              <MDButton
                variant="text"
                color={darkMode ? "white" : "dark"}
                onClick={() => setIsEdit(true)}
              >
                <Icon>edit</Icon>&nbsp;{t("accountUi.edit")}
              </MDButton>
            )}
          </MDBox>
        </MDBox>
        <MDBox mb={1} lineHeight={0}>
          <MDTypography variant="caption" color="text">
            {t("accountUi.addressLabel")}&nbsp;
            {isEdit ? (
              <MDInput
                type="text"
                label={t("accountUi.address")}
                defaultValue={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            ) : (
              <MDTypography variant="caption" fontWeight="medium" textTransform="capitalize">
                {address}
              </MDTypography>
            )}
          </MDTypography>
        </MDBox>
        <MDBox mb={1} lineHeight={0}>
          <MDTypography variant="caption" color="text">
            {t("accountUi.telephoneLabel")}&nbsp;
            {isEdit ? (
              <MDInput
                type="text"
                label={t("accountUi.telephone")}
                defaultValue={telephone}
                onChange={(e) => setTelephone(e.target.value)}
              />
            ) : (
              <MDTypography variant="caption" fontWeight="medium">
                {telephone}
              </MDTypography>
            )}
          </MDTypography>
        </MDBox>
        <MDBox mb={1} lineHeight={0}>
          <MDTypography variant="caption" color="text">
            {t("accountUi.postalCodeLabel")}&nbsp;
            {isEdit ? (
              <MDInput
                type="text"
                label={t("accountUi.postalCode")}
                defaultValue={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
            ) : (
              <MDTypography variant="caption" fontWeight="medium">
                {postalCode}
              </MDTypography>
            )}
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of Bill
Basic.defaultProps = {
  noGutter: false,
};

// Typechecking props for the Bill
Basic.propTypes = {
  noGutter: PropTypes.bool,
};

export default Basic;
